import { useState, useEffect } from 'react';
import { useAuthHeader, useAuth } from '@/hooks/useAuth';
import { listJobsV2, downloadFileV2, deleteJobV2 } from '@/lib/api/logistics-shipping-kpi-v2';
import { getProductGroupTypes } from '@/lib/api/product-group-type';
import type { LogisticsShippingKpiJobV2 } from '@/lib/types/feature/logistics-shipping-kpi-v2';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Loader2,
  Download,
  Eye,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  ChevronDown,
  ChevronUp,
  Trash2,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

// エラーメッセージの切り詰め用ユーティリティ
const truncateErrorMessage = (
  message: string,
  maxLength: number = 500,
): { truncated: string; isTruncated: boolean } => {
  if (!message || message.length <= maxLength) {
    return { truncated: message, isTruncated: false };
  }

  // 最後の改行位置を探して、そこで切り詰める（可読性向上）
  const truncated = message.substring(0, maxLength);
  const lastNewlineIndex = truncated.lastIndexOf('\n');
  const cutPoint = lastNewlineIndex > maxLength * 0.7 ? lastNewlineIndex : maxLength;

  return {
    truncated: message.substring(0, cutPoint) + '...',
    isTruncated: true,
  };
};

// エラーメッセージ表示コンポーネント
interface ErrorMessageDisplayProps {
  errorMessage: string;
}

function ErrorMessageDisplay({ errorMessage }: ErrorMessageDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { truncated, isTruncated } = truncateErrorMessage(errorMessage, 500);

  return (
    <div>
      <h3 className="font-semibold mb-2 text-red-600">エラーメッセージ</h3>
      <div className="bg-red-50 border border-red-200 rounded p-3">
        <div className="text-sm font-mono whitespace-pre-wrap break-words overflow-hidden">
          {isExpanded ? errorMessage : truncated}
        </div>
        {isTruncated && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 h-6 px-2 text-xs text-red-600 hover:text-red-800 hover:bg-red-100"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-3 h-3 mr-1" />
                省略表示
              </>
            ) : (
              <>
                <ChevronDown className="w-3 h-3 mr-1" />
                もっと見る ({errorMessage.length.toLocaleString()}文字)
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

interface LogisticsShippingKpiJobsV2Props {
  account: {
    id: string;
    code: string;
    name: string;
  };
}

export function LogisticsShippingKpiJobsV2({ account }: LogisticsShippingKpiJobsV2Props) {
  const { getAuthHeader, isLoading: authHeaderLoading } = useAuthHeader();
  const { user } = useAuth();
  const { toast } = useToast();
  const [jobs, setJobs] = useState<LogisticsShippingKpiJobV2[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());
  const [deletingJobs, setDeletingJobs] = useState<Set<string>>(new Set());
  const [showOnlyMyJobs, setShowOnlyMyJobs] = useState(true); // デフォルト：自分のジョブのみ表示
  const [productGroupTypeNames, setProductGroupTypeNames] = useState<Record<string, string>>({});

  // 商品グループタイプ名を取得
  useEffect(() => {
    const fetchProductGroupTypes = async () => {
      if (authHeaderLoading) return;
      try {
        const authHeaders = getAuthHeader();
        const types = await getProductGroupTypes(account.id, authHeaders);
        const nameMap: Record<string, string> = {};
        for (const t of types) {
          nameMap[t.id] = t.name;
        }
        setProductGroupTypeNames(nameMap);
      } catch {
        // 商品グループタイプ名の取得失敗は表示に影響するだけなので無視
      }
    };
    fetchProductGroupTypes();
  }, [account.id, authHeaderLoading]);

  // ジョブ一覧を取得
  const fetchJobs = async () => {
    if (authHeaderLoading) return;

    try {
      setLoading(true);
      setError(null);
      const authHeaders = getAuthHeader();

      // 自分のジョブのみ表示する場合はログインユーザーのemailを渡す
      const executedBy = showOnlyMyJobs ? user?.email : undefined;

      const jobList = await listJobsV2(account.id, authHeaders, 50, executedBy || undefined);
      setJobs(jobList);
    } catch {
      setError('ジョブ一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [account.id, authHeaderLoading, showOnlyMyJobs, user?.email]);

  // ファイルダウンロード処理
  const handleFileDownload = async (jobId: string, fileType: string, fileName?: string) => {
    if (!fileName) {
      setError('ファイルが存在しません');
      return;
    }

    const downloadKey = `${jobId}-${fileType}`;
    if (downloadingFiles.has(downloadKey)) return;

    try {
      setDownloadingFiles((prev) => new Set(prev).add(downloadKey));
      setError(null);
      const authHeaders = getAuthHeader();
      const downloadInfo = await downloadFileV2(account.id, jobId, fileType, authHeaders);

      // 新しいタブでダウンロードURLを開く
      const link = document.createElement('a');
      link.href = downloadInfo.download_url;
      link.download = downloadInfo.file_name;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      setError('ファイルのダウンロードに失敗しました');
    } finally {
      setDownloadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(downloadKey);
        return newSet;
      });
    }
  };

  // ジョブ削除処理
  const handleDeleteJob = async (jobId: string) => {
    if (authHeaderLoading || deletingJobs.has(jobId)) return;

    try {
      setDeletingJobs((prev) => new Set(prev).add(jobId));
      setError(null);
      const authHeaders = getAuthHeader();
      await deleteJobV2(account.id, jobId, authHeaders);

      // 削除成功時にトースト表示
      toast({
        title: '削除完了',
        description: '実行ログが正常に削除されました',
      });

      // ジョブ一覧を再読み込み
      await fetchJobs();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'ジョブの削除に失敗しました';
      setError(errorMessage);
      toast({
        title: '削除失敗',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setDeletingJobs((prev) => {
        const newSet = new Set(prev);
        newSet.delete(jobId);
        return newSet;
      });
    }
  };

  // ジョブステータスのバッジ表示
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <Badge variant="default" className="bg-green-500">
            <CheckCircle className="w-3 h-3 mr-1" />
            完了
          </Badge>
        );
      case 'running':
        return (
          <Badge variant="default" className="bg-blue-500">
            <Clock className="w-3 h-3 mr-1" />
            実行中
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="destructive">
            <XCircle className="w-3 h-3 mr-1" />
            失敗
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="secondary">
            <AlertCircle className="w-3 h-3 mr-1" />
            待機中
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // 実行開始日時のフォーマット
  const formatExecutionStartTime = (dateTime?: string) => {
    if (!dateTime) return '-';
    try {
      const date = new Date(dateTime);
      const dateStr = format(date, 'yyyy-MM-dd', { locale: ja });
      const timeStr = format(date, 'HH:mm', { locale: ja });
      return { dateStr, timeStr };
    } catch {
      return '-';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          倉庫出荷効果検証 実行ログ
          <Button onClick={fetchJobs} disabled={loading} size="sm">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            更新
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="show-only-my-jobs-v2"
              checked={showOnlyMyJobs}
              onCheckedChange={(checked) => setShowOnlyMyJobs(checked === true)}
            />
            <label
              htmlFor="show-only-my-jobs-v2"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              自分のジョブのみ表示
            </label>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading && jobs.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            読み込み中...
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">実行ログがありません</div>
        ) : (
          <div className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ステータス</TableHead>
                  <TableHead>実行者</TableHead>
                  <TableHead>実行開始日時</TableHead>
                  <TableHead>集計期間</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => {
                  const executionTime = formatExecutionStartTime(job.execution_start_time);
                  return (
                    <TableRow key={job.id}>
                      <TableCell>{getStatusBadge(job.job_status)}</TableCell>
                      <TableCell className="font-medium">{job.executed_by}</TableCell>
                      <TableCell className="text-sm">
                        {executionTime === '-' ? (
                          '-'
                        ) : (
                          <div>
                            <div>{executionTime.dateStr}</div>
                            <div>{executionTime.timeStr}</div>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {job.start_date} 〜 {job.end_date}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-4 items-center">
                          <JobDetailDialog
                            job={job}
                            onFileDownload={handleFileDownload}
                            downloadingFiles={downloadingFiles}
                            onDeleteJob={handleDeleteJob}
                            deletingJobs={deletingJobs}
                            currentUser={user}
                            productGroupTypeNames={productGroupTypeNames}
                          />
                          {job.job_status === 'completed' && job.result_files_name && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                handleFileDownload(job.id, 'result_files', job.result_files_name)
                              }
                              disabled={downloadingFiles.has(`${job.id}-result_files`)}
                            >
                              {downloadingFiles.has(`${job.id}-result_files`) ? (
                                <Loader2 className="w-3 h-3 animate-spin mr-1" />
                              ) : (
                                <Download className="w-3 h-3 mr-1" />
                              )}
                              結果
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ジョブ詳細ダイアログコンポーネント
interface JobDetailDialogProps {
  job: LogisticsShippingKpiJobV2;
  onFileDownload: (jobId: string, fileType: string, fileName?: string) => void;
  downloadingFiles: Set<string>;
  onDeleteJob: (jobId: string) => Promise<void>;
  deletingJobs: Set<string>;
  currentUser: { email?: string | null } | null;
  productGroupTypeNames: Record<string, string>;
}

function JobDetailDialog({
  job,
  onFileDownload,
  downloadingFiles,
  onDeleteJob,
  deletingJobs,
  currentUser,
  productGroupTypeNames,
}: JobDetailDialogProps) {
  const [isOpen, setIsOpen] = useState(false);

  const formatDateTime = (dateTime?: string) => {
    if (!dateTime) return '-';
    try {
      return format(new Date(dateTime), 'yyyy年MM月dd日 HH:mm:ss', { locale: ja });
    } catch {
      return dateTime;
    }
  };

  const handleDeleteJob = async () => {
    await onDeleteJob(job.id);
    // 削除成功後にダイアログを閉じる
    setIsOpen(false);
  };

  const FileDownloadButton = ({
    jobId,
    fileType,
    fileName,
    label,
  }: {
    jobId: string;
    fileType: string;
    fileName?: string;
    label: string;
  }) => {
    const downloadKey = `${jobId}-${fileType}`;
    const isDownloading = downloadingFiles.has(downloadKey);

    if (!fileName) {
      return (
        <Button size="sm" variant="outline" disabled>
          {label} (未設定)
        </Button>
      );
    }

    return (
      <Button
        size="sm"
        variant="outline"
        onClick={() => onFileDownload(jobId, fileType, fileName)}
        disabled={isDownloading}
      >
        {isDownloading ? (
          <Loader2 className="w-3 h-3 animate-spin mr-1" />
        ) : (
          <Download className="w-3 h-3 mr-1" />
        )}
        {label}
      </Button>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <Eye className="w-3 h-3 mr-1" />
          詳細
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>ジョブ詳細 - {job.id}</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          {/* 基本情報 */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="font-semibold mb-2">基本情報</h3>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium">実行者:</span> {job.executed_by}
                </div>
                <div>
                  <span className="font-medium">ステータス:</span> {job.job_status}
                </div>
                <div>
                  <span className="font-medium">作成日時:</span> {formatDateTime(job.created_at)}
                </div>
                <div>
                  <span className="font-medium">実行開始:</span>{' '}
                  {formatDateTime(job.execution_start_time)}
                </div>
                <div>
                  <span className="font-medium">実行終了:</span>{' '}
                  {formatDateTime(job.execution_end_time)}
                </div>
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-2">集計条件</h3>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium">集計期間:</span> {job.start_date} 〜 {job.end_date}
                </div>
                <div>
                  <span className="font-medium">比較期間:</span> {job.comparison_start_date} 〜{' '}
                  {job.comparison_end_date}
                </div>
                <div>
                  <span className="font-medium">新商品基準日:</span>{' '}
                  {job.new_product_base_date ?? '未設定'}
                </div>
                <div>
                  <span className="font-medium">展開店舗フィルタ:</span>{' '}
                  {job.apply_dealing_sku_filter ? '適用' : '非適用'}
                </div>
                <div>
                  <span className="font-medium">商品グループサマリー階層:</span>{' '}
                  {job.product_group_summary_depths
                    ? Object.entries(job.product_group_summary_depths)
                        .map(
                          ([typeId, depth]) =>
                            `${productGroupTypeNames[typeId] || typeId}: ${depth}階層`,
                        )
                        .join(', ') || '-'
                    : '-'}
                </div>
                <div>
                  <span className="font-medium">新商品除外タイプ:</span>{' '}
                  {job.new_product_exclusion_type ?? '未設定'}
                </div>
                <div>
                  <span className="font-medium">上位品割合:</span>{' '}
                  {job.top_product_threshold_percent ?? 20}%
                </div>
                <div>
                  <span className="font-medium">非稼働判定期間:</span>{' '}
                  {job.inactive_window_days != null
                    ? `${job.inactive_window_days}日`
                    : '未設定（既定30日を適用）'}
                </div>
              </div>
            </div>
          </div>

          {/* 展開判定パラメータ */}
          <div>
            <h3 className="font-semibold mb-2">展開判定パラメータ</h3>
            {job.apply_dealing_sku_filter ? (
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="font-medium mb-1">在庫</div>
                  <div className="space-y-1">
                    <div>
                      <span className="font-medium">在庫参照直近日数 (recent_stock_day):</span>{' '}
                      {job.recent_stock_day ?? '未設定'}
                    </div>
                    <div>
                      <span className="font-medium">基準在庫数 (required_stock_quantity):</span>{' '}
                      {job.required_stock_quantity ?? '未設定'}
                    </div>
                    <div>
                      <span className="font-medium">在庫日数閾値 (stock_day_threshold):</span>{' '}
                      {job.stock_day_threshold ?? '未設定'}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="font-medium mb-1">売上</div>
                  <div className="space-y-1">
                    <div>
                      <span className="font-medium">売上参照直近日数 (recent_sales_day):</span>{' '}
                      {job.recent_sales_day ?? '未設定'}
                    </div>
                    <div>
                      <span className="font-medium">基準販売数 (required_sales_quantity):</span>{' '}
                      {job.required_sales_quantity ?? '未設定'}
                    </div>
                    <div>
                      <span className="font-medium">販売日数閾値 (sales_day_threshold):</span>{' '}
                      {job.sales_day_threshold ?? '未設定'}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500">展開店舗フィルタがOFFのため未適用</div>
            )}
          </div>

          {/* エラーメッセージ */}
          {job.error_message && <ErrorMessageDisplay errorMessage={job.error_message} />}

          {/* アップロードファイル */}
          <div>
            <h3 className="font-semibold mb-2">アップロードファイル</h3>
            <div className="grid grid-cols-2 gap-2">
              <FileDownloadButton
                jobId={job.id}
                fileType="target_sellers"
                fileName={job.target_sellers_file_name}
                label="対象店舗"
              />
              <FileDownloadButton
                jobId={job.id}
                fileType="excluded_skus"
                fileName={job.excluded_skus_file_name}
                label="除外SKU"
              />
              <FileDownloadButton
                jobId={job.id}
                fileType="excluded_seller_sku_combinations"
                fileName={job.excluded_seller_sku_combinations_file_name}
                label="除外店舗×SKU"
              />
              <FileDownloadButton
                jobId={job.id}
                fileType="shipping_orders"
                fileName={job.shipping_orders_file_name}
                label="出荷指示書"
              />
              <FileDownloadButton
                jobId={job.id}
                fileType="store_warehouse_mapping"
                fileName={job.store_warehouse_mapping_file_name}
                label="出荷元倉庫マッピング"
              />
              <FileDownloadButton
                jobId={job.id}
                fileType="product_group_filter"
                fileName={job.product_group_filter_file_name}
                label="商品グループフィルタ"
              />
              <FileDownloadButton
                jobId={job.id}
                fileType="new_product_exclusion"
                fileName={job.new_product_exclusion_file_name}
                label="新商品除外"
              />
            </div>
          </div>

          {/* 結果ファイル */}
          {job.job_status === 'completed' && job.result_files_name && (
            <div>
              <h3 className="font-semibold mb-2">結果ファイル</h3>
              <FileDownloadButton
                jobId={job.id}
                fileType="result_files"
                fileName={job.result_files_name}
                label="結果ファイル (ZIP)"
              />
            </div>
          )}

          {/* 削除ボタン */}
          {currentUser?.email === job.executed_by && (
            <div className="flex justify-end">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="destructive" disabled={deletingJobs.has(job.id)}>
                    {deletingJobs.has(job.id) ? (
                      <Loader2 className="w-3 h-3 animate-spin mr-1" />
                    ) : (
                      <Trash2 className="w-3 h-3 mr-1" />
                    )}
                    実行ログを削除
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>実行ログの削除確認</AlertDialogTitle>
                    <AlertDialogDescription>
                      この実行ログを削除してもよろしいですか？
                      <br />
                      実行期間: {job.start_date} 〜 {job.end_date}
                      <br />
                      <strong>この操作は取り消すことができません。</strong>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>キャンセル</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeleteJob}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      削除する
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
